/**
 * Bank Accounts Page
 * 
 * หน้าจัดการบัญชีธนาคาร - ออกแบบใหม่ให้เรียบง่าย
 * - Card-based layout
 * - แบ่งตาม บัญชีฝาก / บัญชีถอน
 * - Toggle switches สำหรับ Primary accounts
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Building2,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Wallet,
  RefreshCcw,
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
  CheckCircle,
  XCircle,
  Settings,
  Star,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import { useCurrency } from '@/stores/currency-store'
import { BankAccountService, type BankAccount, type BankAccountStats, type BankListItem, type BankAccountFormData } from '@/domain/bank-account'
import { BankAccountDialog } from './components'
import type { CurrencyCode } from '@/data/banks'
import { toast } from 'sonner'

// Bank logo colors
const bankColors: Record<string, string> = {
  kbank: 'bg-green-600',
  scb: 'bg-purple-600',
  bbl: 'bg-blue-700',
  ktb: 'bg-cyan-600',
  bay: 'bg-yellow-500',
  tmb: 'bg-orange-500',
  ttb: 'bg-orange-500',
  cimb: 'bg-red-600',
  uob: 'bg-blue-500',
  gsb: 'bg-pink-500',
  baac: 'bg-green-700',
  tisco: 'bg-blue-600',
  lhbank: 'bg-teal-600',
  kk: 'bg-yellow-600',
  icbc: 'bg-red-700',
  truewallet: 'bg-orange-500',
  default: 'bg-slate-500',
}

// Format currency
const formatCurrency = (value: number) => {
  return `฿${value.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`
}

export function BanksPage() {
  const { t } = useTranslation()
  const { sessionToken } = useAuth()
  const { currency } = useCurrency()
  
  // State
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [accounts, setAccounts] = useState<BankAccount[]>([])
  const [stats, setStats] = useState<BankAccountStats | null>(null)
  const [activeTab, setActiveTab] = useState<'all' | 'deposit' | 'withdraw'>('all')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [bankList, setBankList] = useState<BankListItem[]>([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editAccount, setEditAccount] = useState<BankAccount | null>(null)
  
  // Confirmation dialogs state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    description: string
    action: () => Promise<void>
    variant?: 'default' | 'destructive'
  }>({
    open: false,
    title: '',
    description: '',
    action: async () => {},
    variant: 'default'
  })
  
  // Load data
  const loadData = async (showLoading = true) => {
    if (!sessionToken) return
    
    if (showLoading) setIsLoading(true)
    else setIsRefreshing(true)
    
    try {
      // ดึง WebBankAccount จาก Parse Server
      const data = await BankAccountService.getAll(sessionToken)
      setAccounts(data)
      setStats(BankAccountService.calculateStats(data))
      
      // ดึง BankList จาก Local Data ตาม currency ที่เลือก
      const localBanks = BankAccountService.getBankListLocal(currency.code as CurrencyCode)
      setBankList(localBanks)
    } catch (error) {
      console.error('Failed to load bank accounts:', error)
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }
  
  useEffect(() => {
    loadData()
  }, [sessionToken, currency.code])
  
  // Open dialog for new account
  const handleAddAccount = () => {
    setEditAccount(null)
    setDialogOpen(true)
  }
  
  // Open dialog for edit
  const handleEditAccount = (account: BankAccount) => {
    setEditAccount(account)
    setDialogOpen(true)
  }
  
  // Save account (create/update)
  const handleSaveAccount = async (data: BankAccountFormData): Promise<boolean> => {
    if (!sessionToken) return false
    
    try {
      // เตรียมข้อมูลสำหรับส่ง
      const saveData: any = {
        ...data,
      }
      
      // ถ้าเป็นการแก้ไข ให้ส่ง id ไปด้วย
      if (editAccount?.id) {
        saveData.id = editAccount.id
      }
      
      const result = await BankAccountService.save(sessionToken, saveData)
      
      if (result.success) {
        toast.success(editAccount ? 'บันทึกข้อมูลสำเร็จ' : 'เพิ่มบัญชีธนาคารสำเร็จ')
        await loadData(false)
        return true
      } else {
        toast.error(result.error || 'เกิดข้อผิดพลาด')
        return false
      }
    } catch (error: any) {
      console.error('Save account error:', error)
      toast.error(error.message || 'เกิดข้อผิดพลาดในการบันทึก')
      return false
    }
  }
  
  // Filter accounts by tab
  const filteredAccounts = accounts.filter(account => {
    if (activeTab === 'all') return true
    if (activeTab === 'deposit') return account.type === 'Deposit' || account.type === 'Both'
    if (activeTab === 'withdraw') return account.type === 'Withdraw' || account.type === 'Both'
    return true
  })
  
  // Deposit accounts (for primary toggle)
  const depositAccounts = accounts.filter(a => a.type === 'Deposit' || a.type === 'Both')
  const withdrawAccounts = accounts.filter(a => a.type === 'Withdraw' || a.type === 'Both')
  
  // Toggle primary deposit with confirmation
  const handleTogglePrimaryDeposit = (id: string) => {
    const account = accounts.find(a => a.id === id)
    if (!account) return
    
    const isEnabling = !account.primaryDeposit
    const isTrueWallet = account.banklist_sid === 19 || account.bank_id === 'truewallet'
    
    setConfirmDialog({
      open: true,
      title: isEnabling ? 'ตั้งเป็นบัญชีฝากหลัก' : 'ยกเลิกบัญชีฝากหลัก',
      description: isEnabling 
        ? `ต้องการตั้ง "${account.bank_name} (${account.bank_tag})" เป็นบัญชีฝากหลักหรือไม่?${isTrueWallet ? '\n\nหมายเหตุ: TrueWallet สามารถเป็นฝากหลักได้หลายบัญชี แต่ในกลุ่ม TrueWallet เองมีได้แค่ 1 อัน' : '\n\nหมายเหตุ: จะปิดบัญชีฝากหลักของบัญชีอื่นทั้งหมด (ยกเว้น TrueWallet)'}`
        : `ต้องการยกเลิก "${account.bank_name} (${account.bank_tag})" จากบัญชีฝากหลักหรือไม่?`,
      action: async () => {
        if (!sessionToken) return
        try {
          await BankAccountService.togglePrimaryDeposit(sessionToken, id)
          toast.success(isEnabling ? 'ตั้งเป็นบัญชีฝากหลักแล้ว' : 'ยกเลิกบัญชีฝากหลักแล้ว')
          await loadData(false)
        } catch (error: any) {
          toast.error(error.message || 'เกิดข้อผิดพลาด')
        }
      },
      variant: 'default'
    })
  }
  
  // Toggle primary withdraw with confirmation
  const handleTogglePrimaryWithdraw = (id: string) => {
    const account = accounts.find(a => a.id === id)
    if (!account) return
    
    // ✅ TrueWallet ไม่สามารถเป็น primaryWithdraw ได้เลย
    const isTrueWallet = account.banklist_sid === 19 || account.bank_id === 'truewallet'
    if (isTrueWallet && !account.primaryWithdraw) {
      toast.error('TrueWallet ไม่สามารถเป็นบัญชีถอนหลักได้')
      return
    }
    
    const isEnabling = !account.primaryWithdraw
    
    setConfirmDialog({
      open: true,
      title: isEnabling ? 'ตั้งเป็นบัญชีถอนหลัก' : 'ยกเลิกบัญชีถอนหลัก',
      description: isEnabling 
        ? `ต้องการตั้ง "${account.bank_name} (${account.bank_tag})" เป็นบัญชีถอนหลักหรือไม่?\n\nหมายเหตุ: จะปิดบัญชีถอนหลักของบัญชีอื่นทั้งหมด`
        : `ต้องการยกเลิก "${account.bank_name} (${account.bank_tag})" จากบัญชีถอนหลักหรือไม่?`,
      action: async () => {
        if (!sessionToken) return
        try {
          await BankAccountService.togglePrimaryWithdraw(sessionToken, id)
          toast.success(isEnabling ? 'ตั้งเป็นบัญชีถอนหลักแล้ว' : 'ยกเลิกบัญชีถอนหลักแล้ว')
          await loadData(false)
        } catch (error: any) {
          toast.error(error.message || 'เกิดข้อผิดพลาด')
        }
      },
      variant: 'default'
    })
  }
  
  // Toggle API status with confirmation
  const handleToggleApiStatus = (id: string) => {
    const account = accounts.find(a => a.id === id)
    if (!account) return
    
    const isEnabling = account.api_status !== 'active'
    
    setConfirmDialog({
      open: true,
      title: isEnabling ? 'เปิดใช้งาน API' : 'ปิดการใช้งาน API',
      description: isEnabling 
        ? `ต้องการเปิดใช้งาน API สำหรับ "${account.bank_name} (${account.bank_tag})" หรือไม่?`
        : `ต้องการปิดการใช้งาน API สำหรับ "${account.bank_name} (${account.bank_tag})" หรือไม่? การปิด API อาจทำให้ระบบไม่สามารถดึงข้อมูลอัตโนมัติได้`,
      action: async () => {
        if (!sessionToken) return
        const success = await BankAccountService.toggleApiStatus(sessionToken, id)
        if (success) {
          toast.success(isEnabling ? 'เปิดใช้งาน API แล้ว' : 'ปิดการใช้งาน API แล้ว')
          await loadData(false)
        } else {
          toast.error('เกิดข้อผิดพลาด')
        }
      },
      variant: isEnabling ? 'default' : 'destructive'
    })
  }
  
  // Delete account
  const handleDelete = async () => {
    if (!sessionToken || !deleteId) return
    const success = await BankAccountService.delete(sessionToken, deleteId)
    if (success) {
      toast.success('ลบบัญชีธนาคารสำเร็จ')
      setDeleteId(null)
      await loadData(false)
    } else {
      toast.error('เกิดข้อผิดพลาดในการลบ')
    }
  }
  
  // Execute confirmation action
  const handleConfirmAction = async () => {
    await confirmDialog.action()
    setConfirmDialog(prev => ({ ...prev, open: false }))
  }
  
  // Get bank color (fallback to predefined colors if not in data)
  const getBankColor = (bankId: string, bankColor?: string) => {
    if (bankColor) return bankColor
    return bankColors[bankId?.toLowerCase()] || bankColors.default
  }
  
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            {t('banks.title')}
          </h1>
          <p className="text-muted-foreground">{t('banks.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => loadData(false)}
            disabled={isRefreshing}
          >
            <RefreshCcw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
            {t('common.refresh')}
          </Button>
          <Button className="gap-2" onClick={handleAddAccount}>
            <Plus className="h-4 w-4" />
            {t('banks.addAccount')}
          </Button>
        </div>
      </div>
      
      {/* Stats Summary */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/50 bg-gradient-to-br from-slate-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-slate-500/10">
                <Wallet className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('banks.totalAccounts')}</p>
                <p className="text-2xl font-bold">{stats?.totalAccounts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border/50 bg-gradient-to-br from-emerald-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <ArrowDownToLine className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('banks.depositBalance')}</p>
                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(stats?.depositBalance || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border/50 bg-gradient-to-br from-rose-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-rose-500/10">
                <ArrowUpFromLine className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('banks.withdrawBalance')}</p>
                <p className="text-xl font-bold text-rose-600 dark:text-rose-400">
                  {formatCurrency(stats?.withdrawBalance || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border/50 bg-gradient-to-br from-blue-500/10 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Wallet className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('banks.totalBalance')}</p>
                <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(stats?.totalBalance || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all" className="gap-2">
            <Building2 className="h-4 w-4" />
            {t('banks.tabs.all')} ({accounts.length})
          </TabsTrigger>
          <TabsTrigger value="deposit" className="gap-2">
            <ArrowDownToLine className="h-4 w-4" />
            {t('banks.tabs.deposit')} ({depositAccounts.length})
          </TabsTrigger>
          <TabsTrigger value="withdraw" className="gap-2">
            <ArrowUpFromLine className="h-4 w-4" />
            {t('banks.tabs.withdraw')} ({withdrawAccounts.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-6">
          {filteredAccounts.length === 0 ? (
            <Card className="py-12 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">{t('banks.noAccounts')}</p>
              <p className="text-sm text-muted-foreground">{t('banks.noAccountsHint')}</p>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredAccounts.map((account) => (
                <BankAccountCard
                  key={account.id}
                  account={account}
                  activeTab={activeTab} // ✅ ส่ง activeTab prop
                  onTogglePrimaryDeposit={handleTogglePrimaryDeposit}
                  onTogglePrimaryWithdraw={handleTogglePrimaryWithdraw}
                  onToggleApiStatus={handleToggleApiStatus}
                  onDelete={(id) => setDeleteId(id)}
                  onEdit={handleEditAccount}
                  getBankColor={getBankColor}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('banks.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('banks.confirmDeleteDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('banks.deleteAccount')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* General Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmAction}
              className={confirmDialog.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              ยืนยัน
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Bank Account Dialog */}
      <BankAccountDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        account={editAccount}
        bankList={bankList}
        onSave={handleSaveAccount}
      />
    </div>
  )
}

// ============================================
// Bank Account Card Component
// ============================================

interface BankAccountCardProps {
  account: BankAccount
  activeTab: 'all' | 'deposit' | 'withdraw' // ✅ เพิ่ม activeTab prop
  onTogglePrimaryDeposit: (id: string) => void
  onTogglePrimaryWithdraw: (id: string) => void
  onToggleApiStatus: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (account: BankAccount) => void
  getBankColor: (bankId: string, bankColor?: string) => string
}

function BankAccountCard({
  account,
  activeTab,
  onTogglePrimaryDeposit,
  onTogglePrimaryWithdraw,
  onToggleApiStatus,
  onDelete,
  onEdit,
  getBankColor,
}: BankAccountCardProps) {
  const { t } = useTranslation()
  const isDeposit = account.type === 'Deposit' || account.type === 'Both'
  const isWithdraw = account.type === 'Withdraw' || account.type === 'Both'
  
  // ✅ ตรวจสอบว่าเป็น TrueWallet หรือไม่
  const isTrueWallet = account.banklist_sid === 19 || account.bank_id === 'truewallet'
  
  // ✅ แสดง toggle ตาม activeTab
  // - tab = "deposit" → แสดงเฉพาะ "บัญชีฝากหลัก"
  // - tab = "withdraw" → แสดงเฉพาะ "บัญชีถอนหลัก"
  // - tab = "all" → แสดงทั้งสอง (ถ้า account type รองรับ)
  const showPrimaryDeposit = activeTab === 'all' || activeTab === 'deposit'
  const showPrimaryWithdraw = (activeTab === 'all' || activeTab === 'withdraw') && !isTrueWallet // ✅ TrueWallet ไม่แสดง toggle ถอนหลัก
  
  // ใช้สีจาก bank_color ถ้ามี ไม่งั้นใช้ class
  const bankColorStyle = account.bank_color 
    ? { backgroundColor: account.bank_color } 
    : undefined
  const bankColorClass = account.bank_color 
    ? '' 
    : getBankColor(account.bank_id, account.bank_color)
  
  return (
    <Card className="border-border/50 overflow-hidden hover:shadow-lg transition-shadow">
      {/* Color stripe */}
      <div 
        className={cn('h-1.5', bankColorClass)} 
        style={bankColorStyle}
      />
      
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {/* Bank logo */}
            {account.image_url ? (
              <img 
                src={account.image_url} 
                alt={account.bank_name}
                className="w-11 h-11 rounded-xl object-cover"
                onError={(e) => {
                  // Fallback ถ้าโหลดรูปไม่ได้
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.nextElementSibling?.classList.remove('hidden')
                }}
              />
            ) : null}
            <div 
              className={cn(
                'w-11 h-11 rounded-xl flex items-center justify-center',
                account.image_url ? 'hidden' : '',
                bankColorClass
              )}
              style={account.bank_color ? { backgroundColor: account.bank_color } : undefined}
            >
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {account.bank_name}
                {account.bank_tag && (
                  <Badge variant="outline" className="text-xs">
                    {account.bank_tag}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="font-mono text-xs">
                {account.bank_account}
              </CardDescription>
            </div>
          </div>
          
          {/* Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                className="gap-2 cursor-pointer"
                onClick={() => onEdit(account)}
              >
                <Edit className="h-4 w-4" />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer">
                <Settings className="h-4 w-4" />
                {t('banks.apiSettings')}
              </DropdownMenuItem>
              {account.show_transfer && (
                <DropdownMenuItem className="gap-2 cursor-pointer">
                  <Send className="h-4 w-4" />
                  {t('banks.transfer')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="gap-2 text-red-500 cursor-pointer"
                onClick={() => onDelete(account.id)}
              >
                <Trash2 className="h-4 w-4" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Account holder */}
        <div>
          <p className="text-sm text-muted-foreground">{t('banks.accountHolder')}</p>
          <p className="font-medium">{account.name}</p>
        </div>
        
        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          {/* Type badge */}
          <Badge variant={account.type === 'Both' ? 'default' : account.type === 'Deposit' ? 'success' : 'warning'}>
            {t(`banks.type.${account.type.toLowerCase()}`)}
          </Badge>
          
          {/* Status badge */}
          <Badge variant={account.status === 'Active' ? 'success' : 'secondary'}>
            {t(`banks.status.${account.status.toLowerCase()}`)}
          </Badge>
          
          {/* Maintenance badge */}
          {account.bankMaintenance && (
            <Badge variant="warning" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              {t('banks.status.maintenance')}
            </Badge>
          )}
        </div>
        
        {/* Balance */}
        <div className="p-3 rounded-lg bg-muted/30">
          <p className="text-xs text-muted-foreground mb-1">{t('banks.balance')}</p>
          <p className="text-2xl font-bold">
            {formatCurrency(account.amount)}
          </p>
        </div>
        
        {/* API Status */}
        <div className="flex flex-wrap gap-2 text-xs">
          <button 
            onClick={() => onToggleApiStatus(account.id)}
            className="focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-full"
          >
            <StatusPill label="API" active={account.api_status === 'active'} clickable />
          </button>
          <StatusPill label="Bank" active={account.bankApi} />
          <StatusPill label="Slip" active={account.slipApi} />
          <StatusPill label="LINE" active={account.lineconnectApi} />
          <StatusPill label="GW" active={account.gatewayApi} />
        </div>
        
        {/* Primary toggles */}
        <div className="pt-3 border-t border-border/50 space-y-3">
          {/* ✅ แสดง "บัญชีฝากหลัก" เมื่อ: tab = "all" หรือ "deposit" และ account รองรับฝาก */}
          {isDeposit && showPrimaryDeposit && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className={cn('h-4 w-4', account.primaryDeposit ? 'text-emerald-500 fill-emerald-500' : 'text-muted-foreground')} />
                <span className="text-sm">{t('banks.primaryDeposit')}</span>
              </div>
              <Switch
                checked={account.primaryDeposit}
                onCheckedChange={() => onTogglePrimaryDeposit(account.id)}
              />
            </div>
          )}
          
          {/* ✅ แสดง "บัญชีถอนหลัก" เมื่อ: tab = "all" หรือ "withdraw" และ account รองรับถอน และไม่ใช่ TrueWallet */}
          {isWithdraw && showPrimaryWithdraw && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className={cn('h-4 w-4', account.primaryWithdraw ? 'text-rose-500 fill-rose-500' : 'text-muted-foreground')} />
                <span className="text-sm">{t('banks.primaryWithdraw')}</span>
              </div>
              <Switch
                checked={account.primaryWithdraw}
                onCheckedChange={() => onTogglePrimaryWithdraw(account.id)}
                disabled={isTrueWallet} // ✅ Disable สำหรับ TrueWallet
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Helper Components
// ============================================

function StatusPill({ label, active, clickable }: { label: string; active: boolean; clickable?: boolean }) {
  return (
    <div className={cn(
      'flex items-center gap-1 px-2 py-0.5 rounded-full border transition-all',
      active 
        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
        : 'bg-muted/50 border-border text-muted-foreground',
      clickable && 'cursor-pointer hover:opacity-80 hover:shadow-sm'
    )}>
      {active ? (
        <CheckCircle className="h-3 w-3" />
      ) : (
        <XCircle className="h-3 w-3" />
      )}
      {label}
    </div>
  )
}
